/**
 * エンジンの選択（FR-FLIP-03）。
 *
 * 'auto' は WebGL が使えれば 3D、使えなければ 2D。
 * 'webgl3d' を明示指定していても WebGL が無ければ 2D へ降格する — 閲覧できないより、
 * 見た目を落としてでも読めることを優先する（NFR-COMPAT-01）。
 * 公開 fallback イベントの発火は core（Viewer）の担当で、ここは判断だけを返す。
 */
import type { RenderEngine } from '../core/engine';
import type { EngineFailureReason } from '../core/events';
import type { EngineKind } from '../core/options';
import { Css2dEngine } from './css2d/css2d-engine';
import { isWebglSupported } from './webgl3d/support';
import { Webgl3dEngine } from './webgl3d/webgl3d-engine';

export interface EngineChoice {
  engine: RenderEngine;
  /** 実際に使うエンジン（'auto' は解決済み）。 */
  kind: Exclude<EngineKind, 'auto'>;
  /** 3D を諦めた理由（降格したときだけ入る）。 */
  reason?: EngineFailureReason;
}

/** 要求されたエンジン種別から、実際に使うエンジンを決める。 */
export function selectEngine(want: EngineKind, doc: Document): EngineChoice {
  if (want === 'css2d') return { engine: new Css2dEngine(), kind: 'css2d' };
  if (!isWebglSupported(doc)) {
    return { engine: new Css2dEngine(), kind: 'css2d', reason: 'unsupported' };
  }
  return { engine: new Webgl3dEngine(), kind: 'webgl3d' };
}

/**
 * 降格先の 2D エンジンを作る（マウント失敗・実行中の GL コンテキスト消失からの復帰用）。
 *
 * 通常の選択経路と同じく、具象エンジンを知るのはこのモジュールに閉じる（core は知らない）。
 */
export function createFallbackEngine(): RenderEngine {
  return new Css2dEngine();
}
